import React, { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, CircularProgress, Alert, Button, Tabs, Tab,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";
import SummaryViewCard from "./SummaryViewCard";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#0a0e27", accent: "#637cdb", accentDark: "#3F6AC0" };

// Age isn't stored anywhere — derived from DOB for the review dialog (2026-09-03).
function _ageFromDob(dob) {
  if (!dob) return "—";
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return "—";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const hasHadBirthdayThisYear =
    today.getMonth() > birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
  if (!hasHadBirthdayThisYear) age -= 1;
  return age >= 0 ? `${age}` : "—";
}

export default function TelehealthReviewDashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const staff = JSON.parse(localStorage.getItem("telehealthReviewer") || "null");

  const [tab, setTab] = useState(0);
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [reviewing, setReviewing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [comments, setComments] = useState("");
  const [signing, setSigning] = useState(false);

  const bg = isDark ? "#0a0f1e" : "#f0f4ff";
  const cardBg = isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.68)";
  const headers = { Authorization: `Bearer ${staff?.token}` };

  const load = async () => {
    try {
      const [q, h] = await Promise.all([
        axios.get(`${API_BASE}/telehealth-review/queue`, { headers }),
        axios.get(`${API_BASE}/telehealth-review/history`, { headers }),
      ]);
      setQueue(q.data.queue || []);
      setHistory(h.data.history || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("telehealthReviewer");
        navigate("/telehealth-review-login", { replace: true });
        return;
      }
      setError(err.response?.data?.detail || "Failed to load the review queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!staff?.token) { navigate("/telehealth-review-login", { replace: true }); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClaim = async (order) => {
    setBusyId(order._id);
    setError(null);
    try {
      await axios.post(`${API_BASE}/telehealth-review/queue/${order._id}/claim`, {}, { headers });
      await openReview(order._id);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to claim this order.");
    } finally {
      setBusyId(null);
    }
  };

  const handleUnclaim = async (order) => {
    setBusyId(order._id);
    setError(null);
    try {
      await axios.post(`${API_BASE}/telehealth-review/queue/${order._id}/unclaim`, {}, { headers });
      if (reviewing === order._id) { setReviewing(null); setDetail(null); }
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to release this order.");
    } finally {
      setBusyId(null);
    }
  };

  const openReview = async (orderId) => {
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/telehealth-review/queue/${orderId}`, { headers });
      setDetail(res.data);
      setReviewing(orderId);
      setComments("");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load this order.");
    }
  };

  const handleViewPendingReport = async (orderId) => {
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/telehealth-review/queue/${orderId}/pending-report`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load the generated report.");
    }
  };

  const handleSign = async () => {
    if (!comments.trim()) return;
    setSigning(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/telehealth-review/queue/${reviewing}/sign`, { comments }, { headers });
      setReviewing(null);
      setDetail(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit your review.");
    } finally {
      setSigning(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("telehealthReviewer");
    navigate("/telehealth-review-login", { replace: true });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: BRAND.accent }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, position: "relative", overflow: "hidden" }}>
      {/* Animated background blobs — same treatment as the main app's login/glass pages */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-12%", left: "-6%", size: 480, color: isDark ? "rgba(10,14,39,0.18)" : "rgba(10,14,39,0.06)", delay: 0 },
          { top: "50%", right: "-10%", size: 420, color: isDark ? "rgba(99,124,219,0.07)" : "rgba(99,124,219,0.1)", delay: 1.2 },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%",
            background: b.color, filter: "blur(90px)",
          }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 9 + i * 2, repeat: Infinity, delay: b.delay, ease: "easeInOut" }}
          />
        ))}
      </Box>

      <Box sx={{ position: "relative", zIndex: 10, px: { xs: 2, md: 5 }, py: 4 }}>
        <motion.div initial={{ y: -16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.45, ease: "easeOut" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Box sx={{ display: "inline-flex", alignItems: "center", bgcolor: BRAND.navy, borderRadius: "8px", px: 1.25, py: 0.75, mb: 1.5 }}>
                <img src="/logo-light.png" alt="Proove Genomics" style={{ height: 20, width: "auto", display: "block" }} />
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", color: BRAND.accent, mb: 0.5 }}>
                TELEHEALTH REVIEW
              </Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
                Clinician Review Queue
              </Typography>
              <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", mt: 0.5 }}>
                Signed in as Dr. {staff?.name || staff?.email}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ThemeToggle />
              <Button variant="outlined" onClick={handleLogout} sx={{ textTransform: "none", borderRadius: "10px" }}>Log out</Button>
            </Stack>
          </Box>
        </motion.div>

        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
          <Box sx={{ mb: 3 }}>
            <SummaryViewCard to="/telehealth-summary-view" />
          </Box>
        </motion.div>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label={`Queue (${queue.length})`} sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label={`My Reviewed (${history.length})`} sx={{ textTransform: "none", fontWeight: 700 }} />
        </Tabs>

        {tab === 0 && (
          <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <TableContainer sx={{
              borderRadius: "16px", overflow: "hidden", overflowX: "auto",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
              bgcolor: cardBg, backdropFilter: "blur(20px) saturate(140%)",
              WebkitBackdropFilter: "blur(20px) saturate(140%)",
              boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.4)" : "0 24px 80px rgba(10,14,39,0.08)",
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 11, color: isDark ? "#94a3b8" : "#64748b", borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}` } }}>
                    <TableCell>Customer ID</TableCell>
                    <TableCell>Child</TableCell>
                    <TableCell>Ordered</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queue.map((o) => (
                    <TableRow key={o._id} hover sx={{ "& td": { borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(10,14,39,0.05)"}` } }}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{o.customer_id}</TableCell>
                      <TableCell>{o.child_first_name} {o.child_last_name}</TableCell>
                      <TableCell>{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</TableCell>
                      <TableCell>
                        {o.review_status === "claimed" && !o.claimed_by_me && (
                          <Chip size="small" label={`Claimed by ${o.reviewing_clinician_name || "another clinician"}`} color="default" />
                        )}
                        {o.review_status === "claimed" && o.claimed_by_me && (
                          <Chip size="small" label="Claimed by you" color="info" />
                        )}
                        {o.review_status === "pending_review" && (
                          <Chip size="small" label="Unclaimed" color="warning" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {o.review_status === "pending_review" && (
                          <Button size="small" variant="contained" disabled={busyId === o._id}
                            onClick={() => handleClaim(o)} sx={{ textTransform: "none", borderRadius: "8px", background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.accentDark} 100%)` }}>
                            Claim &amp; Review
                          </Button>
                        )}
                        {o.claimed_by_me && (
                          <>
                            <Button size="small" variant="outlined" onClick={() => openReview(o._id)} sx={{ textTransform: "none", borderRadius: "8px", mr: 1 }}>
                              Continue Review
                            </Button>
                            <Button size="small" color="inherit" disabled={busyId === o._id}
                              onClick={() => handleUnclaim(o)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                              Release
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {queue.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                        Nothing waiting for review right now.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        )}

        {tab === 1 && (
          <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <TableContainer sx={{
              borderRadius: "16px", overflow: "hidden", overflowX: "auto",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
              bgcolor: cardBg, backdropFilter: "blur(20px) saturate(140%)",
              WebkitBackdropFilter: "blur(20px) saturate(140%)",
              boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.4)" : "0 24px 80px rgba(10,14,39,0.08)",
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 11, color: isDark ? "#94a3b8" : "#64748b", borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}` } }}>
                    <TableCell>Customer ID</TableCell>
                    <TableCell>Child</TableCell>
                    <TableCell>Signed</TableCell>
                    <TableCell>Comments</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((o) => (
                    <TableRow key={o._id} hover sx={{ "& td": { borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(10,14,39,0.05)"}` } }}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{o.customer_id}</TableCell>
                      <TableCell>{o.child_first_name} {o.child_last_name}</TableCell>
                      <TableCell>{o.clinician_signed_at ? new Date(o.clinician_signed_at).toLocaleString() : "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12.5, maxWidth: 320 }}>{o.clinician_comments}</TableCell>
                    </TableRow>
                  ))}
                  {history.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                        You haven't reviewed any orders yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        )}

        <Dialog
          open={Boolean(reviewing)} onClose={() => setReviewing(null)} maxWidth="sm" fullWidth
          PaperProps={{ sx: {
            borderRadius: "18px",
            bgcolor: isDark ? "rgba(17,24,39,0.92)" : "rgba(255,255,255,0.96)",
            backdropFilter: "blur(20px) saturate(140%)",
          } }}
        >
          <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: BRAND.accent, textTransform: "uppercase", mb: 0.25 }}>
              Order Review
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
              {detail?.order?.child_first_name} {detail?.order?.child_last_name}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)" }}>
            {detail && (
              <>
                {/* Demographic sections (2026-09-03) — was one 4-cell grid
                    missing gender, race, age, and every parent/guardian
                    field; now four clearly labeled sections, same card
                    treatment as the rest of the page. */}
                {[
                  { title: "Child", rows: [
                    ["Name", `${detail.order.child_first_name || ""} ${detail.order.child_last_name || ""}`.trim() || "—"],
                    ["Date of Birth", detail.order.child_date_of_birth || "—"],
                    ["Age", _ageFromDob(detail.order.child_date_of_birth)],
                    ["Gender", detail.demographics?.child_gender || "—"],
                    ["Race", detail.demographics?.child_race || "—"],
                    ["Customer ID", detail.order.customer_id || "—"],
                  ] },
                  { title: "Parent / Guardian", rows: [
                    ["Name", `${detail.demographics?.parent_first_name || ""} ${detail.demographics?.parent_last_name || ""}`.trim() || "—"],
                    ["Date of Birth", detail.demographics?.parent_date_of_birth || "—"],
                    ["Email", detail.order.parent_email || "—"],
                    ["Phone", detail.demographics?.parent_phone || "—"],
                  ] },
                  { title: "Shipping Address", rows: [
                    ["Street", detail.order.shipping_address?.line1 || "—"],
                    ["City", detail.order.shipping_address?.city || "—"],
                    ["State", detail.order.shipping_address?.state || "—"],
                    ["ZIP", detail.order.shipping_address?.zip || "—"],
                  ] },
                  { title: "Billing Address", rows: [
                    ["Street", detail.order.billing_address?.line1 || "—"],
                    ["City", detail.order.billing_address?.city || "—"],
                    ["State", detail.order.billing_address?.state || "—"],
                    ["ZIP", detail.order.billing_address?.zip || "—"],
                  ] },
                ].map((section) => (
                  <Box key={section.title} sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", color: BRAND.accent, textTransform: "uppercase", mb: 0.75 }}>
                      {section.title}
                    </Typography>
                    <Box sx={{
                      borderRadius: "12px", border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}`,
                      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(10,14,39,0.02)",
                      display: "grid", gridTemplateColumns: "1fr 1fr", overflow: "hidden",
                    }}>
                      {section.rows.map(([label, val], i) => (
                        <Box key={label} sx={{
                          px: 2, py: 1.1,
                          borderBottom: i < section.rows.length - (section.rows.length % 2 === 0 ? 2 : 1)
                            ? `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.06)"}` : "none",
                        }}>
                          <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#64748b" : "#94a3b8", textTransform: "uppercase", mb: 0.25 }}>
                            {label}
                          </Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: isDark ? "#e2e8f0" : "#1e293b", wordBreak: "break-word" }}>{val}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}

                <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 1.25 }}>
                  Questionnaire
                </Typography>
                {detail.questionnaire ? (
                  <Box sx={{
                    borderRadius: "12px", border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}`,
                    mb: 2.5, overflow: "hidden",
                  }}>
                    {[...Object.entries(detail.questionnaire.phenotypes_questions || {}),
                      ...Object.entries(detail.questionnaire.phq_questions || {})].map(([q, a], i) => (
                      <Box key={q} sx={{
                        display: "flex", justifyContent: "space-between", gap: 2, px: 2, py: 1.1,
                        bgcolor: i % 2 === 0 ? (isDark ? "rgba(255,255,255,0.02)" : "rgba(10,14,39,0.015)") : "transparent",
                        borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.05)"}`,
                        "&:last-of-type": { borderBottom: "none" },
                      }}>
                        <Typography sx={{ fontSize: 12.5, color: isDark ? "#cbd5e1" : "#475569" }}>{q}</Typography>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy, flexShrink: 0 }}>{a}</Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mb: 2.5, borderRadius: "10px" }}>No questionnaire data found for this order.</Alert>
                )}

                <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 1.25 }}>
                  AI-Generated Report
                </Typography>
                {detail.report_ready ? (
                  <Box
                    onClick={() => handleViewPendingReport(reviewing)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer",
                      borderRadius: "12px", px: 2, py: 1.5, mb: 2.5,
                      border: `1px solid ${isDark ? "rgba(99,124,219,0.35)" : "rgba(99,124,219,0.3)"}`,
                      bgcolor: isDark ? "rgba(99,124,219,0.1)" : "rgba(99,124,219,0.06)",
                      transition: "background-color 0.15s ease",
                      "&:hover": { bgcolor: isDark ? "rgba(99,124,219,0.16)" : "rgba(99,124,219,0.1)" },
                    }}
                  >
                    <Box sx={{ fontSize: 18 }}>📄</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy }}>
                        View AI-Generated Report
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: isDark ? "#94a3b8" : "#64748b" }}>Opens as PDF in a new tab</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="warning" sx={{ mb: 2.5, borderRadius: "10px" }}>
                    Report not yet generated — waiting on the report-generation pipeline.
                  </Alert>
                )}

                <TextField
                  label="Your comments" multiline minRows={3} fullWidth required
                  value={comments} onChange={(e) => setComments(e.target.value)}
                  placeholder="Clinical notes for this order…"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setReviewing(null)} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button variant="contained" disabled={!comments.trim() || signing} onClick={handleSign}
              sx={{ textTransform: "none", borderRadius: "8px", background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.accentDark} 100%)` }}>
              {signing ? "Signing…" : `Sign as Dr. ${staff?.name || ""}`}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
